import { env } from "@/app/config/env";
import { API_ERRORS, type ApiErrorCode, type ApiResult } from "@shared/contracts";
import { reportError } from "@/app/observability";
import { getAuthToken } from "./auth-client";

/**
 * THE ONLY PLACE THE APP TALKS TO THE SERVICE.
 *
 * Features call a service module, the service module calls this, and nothing else in the
 * app builds a URL or reads a status code. That single choke point is what makes the app
 * survivable on a phone: the timeout, the offline answer, the version floor, the bearer
 * token and the error report are decided once, here, instead of being remembered at forty
 * call sites.
 */
export type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Seconds. A phone changing cells can stall a socket for minutes without erroring. */
  timeout?: number;
};

const DEFAULT_TIMEOUT_SECONDS = 20;

/**
 * A FAILURE AS A THROWN VALUE, for the query cache — which can only see a rejection.
 *
 * The service layer keeps returning `ApiResult` (a value, not a throw) because that is what
 * makes a click handler safe. The queries layer unwraps into this. Both shapes carry the
 * same code, so the app has exactly one error vocabulary.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly fields?: Record<string, string>;
  /** Set once the error has been sent to the reporter, so nothing reports it twice. */
  reported = false;

  constructor(code: ApiErrorCode, message?: string, fields?: Record<string, string>) {
    super(message ?? code);
    this.name = "ApiError";
    this.code = code;
    this.fields = fields;
  }
}

/** Turns a service result into a value or a throw. The queries layer is its only caller. */
export function unwrap<T>(result: ApiResult<T>): T {
  if (result.ok) return result.data;
  throw new ApiError(result.error, result.message, result.fields);
}

type Listener = (code: ApiErrorCode) => void;
const listeners = new Set<Listener>();

/**
 * The one answer that concerns the whole app rather than the caller: this build is too old.
 * (A dead session is handled by the query cache, which can re-check before signing out.)
 */
export function onGlobalApiError(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce(code: ApiErrorCode) {
  if (code === API_ERRORS.APP_TOO_OLD) {
    for (const listener of listeners) listener(code);
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const { method = "GET", body, signal, timeout = DEFAULT_TIMEOUT_SECONDS } = options;
  const token = getAuthToken();

  const headers: Record<string, string> = {
    accept: "application/json",
    /**
     * The service reads these to decide whether this build is still supported, and they
     * turn a support email into a lookup instead of a conversation.
     */
    "x-app-version": env.version,
    "x-app-env": env.appEnv,
  };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers.authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${env.apiUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: signal ?? AbortSignal.timeout(timeout * 1000),
    });
  } catch {
    // No status, no body: DNS, TLS, airplane mode, a captive portal that never answers.
    // NOT reported — a lost packet on a train is not a defect, and one report per dropped
    // request would drown the ones that are.
    return { ok: false, error: API_ERRORS.NETWORK_UNREACHABLE };
  }

  if (response.status === 204) return { ok: true, data: undefined as T };

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    // A 200 with HTML is a hotel Wi-Fi login page, not the service.
    return { ok: false, error: API_ERRORS.NETWORK_UNREACHABLE };
  }

  if (isEnvelope(payload)) {
    if (!payload.ok) {
      announce(payload.error);
      reportServerSide(payload.error, path, response.status, payload.message);
    }
    return payload as ApiResult<T>;
  }

  // Anything that is not the agreed envelope came from in front of the service — a proxy,
  // a load balancer, a WAF. Map by status and move on.
  const code = statusToCode(response.status);
  announce(code);
  reportServerSide(code, path, response.status);
  return { ok: false, error: code };
}

/**
 * REPORTING HAPPENS HERE, not at the call site, and only for failures that mean something
 * is broken. A refused password, a validation error and a 404 are the service working.
 */
function reportServerSide(code: ApiErrorCode, path: string, status: number, message?: string) {
  if (code !== API_ERRORS.SERVER_ERROR) return;
  const error = new ApiError(code, message);
  error.reported = true;
  reportError(error, { path, status, code });
}

function isEnvelope(value: unknown): value is ApiResult<unknown> {
  return typeof value === "object" && value !== null && "ok" in value;
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return API_ERRORS.UNAUTHENTICATED;
  if (status === 403) return API_ERRORS.FORBIDDEN;
  if (status === 404) return API_ERRORS.NOT_FOUND;
  if (status === 409) return API_ERRORS.CONFLICT;
  if (status === 422) return API_ERRORS.VALIDATION_FAILED;
  if (status === 426) return API_ERRORS.APP_TOO_OLD;
  if (status === 429) return API_ERRORS.RATE_LIMITED;
  return API_ERRORS.SERVER_ERROR;
}
